import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Buildings, ArrowRight, CheckCircle, TrendUp } from "@phosphor-icons/react";

const gbp = (n, symbol = "£") =>
  n == null ? "—" : `${symbol}${Number(n).toLocaleString("en-GB")}`;

export default function AddClientModal({ open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); return; }
    setLoading(true);
    api.get("/billing/add-client-preview")
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load pricing"))
      .finally(() => setLoading(false));
  }, [open]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/xero/connect");
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else if (data.auth_url_relative) {
        window.location.href = `${window.location.origin}${data.auth_url_relative}`;
      } else {
        throw new Error("No authorise URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Xero connection");
      setConnecting(false);
    }
  };

  const cycle = preview?.billing_cycle || "monthly";
  const priceOf = (p) => (cycle === "annual" ? p?.annual : p?.monthly);
  const per = cycle === "annual" ? "/yr" : "/mo";
  const requiresChange = preview?.requires_change;
  const enterprise = preview?.enterprise;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-slate-200" data-testid="add-client-modal">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-[#F37A24]/12 flex items-center justify-center mb-2">
            <Buildings size={22} className="text-[#C2410C]" weight="fill" />
          </div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tight text-navy">
            Add a client
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            Connect the client's Xero organisation. You'll sign in to Xero and authorise access, then the
            client appears in your Client Overview with live balances.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center font-mono text-slate-600 text-sm tracking-widest" data-testid="add-client-loading">
            LOADING_PRICING…
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="add-client-impact">
              <div className="flex items-center justify-between text-sm">
                <span className="text-navy/70">Clients connected</span>
                <span className="font-mono font-semibold text-navy tabular" data-testid="add-client-count">
                  {preview.used} → {preview.projected_used}
                </span>
              </div>

              {enterprise ? (
                <div className="mt-3 text-sm text-navy/80" data-testid="add-client-enterprise">
                  This takes you into <span className="font-semibold">Enterprise</span> territory. We'll contact
                  you about pricing — go ahead and connect the client now.
                </div>
              ) : !requiresChange ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-positive font-medium" data-testid="add-client-included">
                  <CheckCircle size={18} weight="fill" />
                  Included in your {preview.current.plan_name} plan — no change to your{" "}
                  {gbp(priceOf(preview.current), preview.symbol)}{per} bill.
                </div>
              ) : (
                <div className="mt-3" data-testid="add-client-price-change">
                  <div className="flex items-center gap-2 text-sm text-navy/80">
                    <TrendUp size={18} className="text-[#C2410C]" weight="fill" />
                    Adding this client moves you to the{" "}
                    <span className="font-semibold text-navy">{preview.projected.plan_name}</span> plan.
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-4">
                    <div className="text-center">
                      <div className="overline text-navy/65">Now</div>
                      <div className="font-heading font-bold text-lg text-navy/70 line-through tabular">
                        {gbp(priceOf(preview.current), preview.symbol)}{per}
                      </div>
                    </div>
                    <ArrowRight size={18} className="text-slate-300" />
                    <div className="text-center">
                      <div className="overline text-[#C2410C]">New</div>
                      <div className="font-heading font-bold text-2xl text-navy tabular" data-testid="add-client-new-price">
                        {gbp(priceOf(preview.projected), preview.symbol)}{per}
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-navy/70 text-center">
                    New price applies from your next billing cycle. Billing starts after your free trial.
                  </p>
                </div>
              )}
            </div>

            {!preview.is_owner && (
              <p className="text-xs text-warn" data-testid="add-client-not-owner">
                Note: your account owner manages billing — connecting a client may change the plan they're charged for.
              </p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button onClick={onClose} data-testid="add-client-cancel"
                className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                Cancel
              </button>
              <button onClick={connect} disabled={connecting} data-testid="add-client-connect-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors disabled:opacity-60">
                {connecting ? "REDIRECTING…" : "Continue to Xero"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
